import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Backdrop } from "@mui/material";
import { Done } from "@mui/icons-material";

import { detectIncognito } from "utils/detectIncognito";
import Logocrom from "assets/logos/froin-3d.svg";
import pageStyles from "./styles";
import { Store, StoreProps } from "hooks/main_store";
import { TextInput } from "components";
import Model from "hooks/Model";
import { returnCurrentLink, verifyEmail } from "utils/validations";
import { authService } from "utils/firebase";
import LocalStorage from "utils/localStorage";
import * as moment from "moment";
import api from "utils/api";

function AuthModal() {
  const styles = pageStyles();
  const { t } = useTranslation();

  const { state } = useContext<StoreProps>(Store);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [requestEmail, setRequestEmail] = useState(false);
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const onSubmit = async () => {
    try {
      Model.setData("loading", true);
      const formatedEmail = email.toLocaleLowerCase().replace(/\s/g, "");

      if (!acceptTerms && !requestEmail) {
        throw new Error(`ERROR: ${t("components.authModal.acceptTerms")}`);
      } else if (!formatedEmail) {
        throw new Error(`ERROR: ${t("pages.home.buyHome.enterValidEmail")}`);
      } else if (!verifyEmail(formatedEmail)) {
        throw new Error(`ERROR: ${t("pages.home.buyHome.formatInvalid")}`);
      } else if (requestEmail) {
        await firstLoad(formatedEmail);
      } else {
        const response = await api.post("/emails/sendAuthOTPEmail", {
          email: formatedEmail.toLocaleLowerCase(),
          url: window.location.origin,
          domain: window.location.host,
        });

        if (response.ok) {
          setEmailSent(true);
          setShowOtpForm(true);
          setEmail(formatedEmail);

          Model.logMetaEvent({
            data: [
              {
                event_name: "CompleteRegistration",
                event_time: parseInt(moment().format("X")),
                action_source: "website",
                original_event_data: {
                  event_name: "CompleteRegistration",
                  event_time: parseInt(moment().format("X")),
                },
                user_data: {
                  client_user_agent: navigator.userAgent,
                  em: [null],
                  ph: [null],
                },
              },
            ],
          });
        } else {
          throw new Error("Error al enviar el correo de acceso.");
        }

        // LocalStorage(result.isPrivate).setItem(
        //   "emailForSignInCrypto",
        //   formatedEmail,
        // );
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const onVerifyOTP = async () => {
    try {
      Model.setData("loading", true);
      const formatedEmail = email.toLocaleLowerCase().replace(/\s/g, "");

      const response = await api.post("/emails/getAuthToken", {
        email: formatedEmail,
        code: otpCode,
      });

      if (!response.ok) {
        throw new Error("Error al obtener el token de autenticación.");
      }

      const token = (response.data as any)?.token;
      if (!token) {
        throw new Error("No se recibió un token válido.");
      }

      const userInfo = await authService.signInWithCustomToken(token);
      Model.updateInfoUser(userInfo.user);
      Model.setData("showAuth", false);
    } catch (error: any) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      Model.setData("loading", false);
    }
  };

  const onAccept = () => {
    setAcceptTerms((prev) => !prev);
  };

  const renderContent = () => {
    if (showOtpForm) {
      return (
        <>
          <p className={styles.message}>
            Ingresa el código de 4 dígitos que enviamos a <span>{email}</span>
          </p>
          <TextInput
            value={otpCode}
            onChange={setOtpCode}
            placeholder="0000"
            maxLength={4}
          />
          <button className={styles.submit} onClick={onVerifyOTP}>
            Verificar código
          </button>
        </>
      );
    }

    if (emailSent) {
      return (
        <p className={styles.message}>
          {t("pages.home.buyHome.checkEmail")} (<span>{email}</span>){" "}
          {t("pages.home.buyHome.andFollow")}
        </p>
      );
    }

    return (
      <>
        <p className={styles.message}>
          {requestEmail
            ? "Por tu seguridad, vuelve a ingresar tu correo para ingresar"
            : "Ya sea tu primera vez o si ya tienes cuenta, ingresa tu correo para acceder a tu perfil"}
        </p>
        <TextInput
          value={email}
          onChange={setEmail}
          placeholder="Tu correo"
          autoComplete="email"
        />
        <button className={styles.submit} onClick={onSubmit}>
          {requestEmail ? "Verificar acceso" : "Enviar email de acceso"}
        </button>
        {!requestEmail && (
          <div className={styles.row}>
            <button
              className={[styles.check, acceptTerms ? styles.checked : ""].join(
                " ",
              )}
              onClick={onAccept}
            >
              <Done className={styles.iconCheck} />
            </button>
            <p className={styles.terms}>
              Confirmo que soy mayor de edad para aceptar los{" "}
              <a
                href={returnCurrentLink("/legal?document=terms")}
                target="_blank"
                className={styles.linkTerms}
                rel="noreferrer"
              >
                Términos &amp; Condiciones
              </a>{" "}
              y la{" "}
              <a
                href={returnCurrentLink("/legal?document=privacy")}
                target="_blank"
                className={styles.linkTerms}
                rel="noreferrer"
              >
                Política de Privacidad
              </a>
            </p>
          </div>
        )}
      </>
    );
  };

  const firstLoad = async (emailString?: string) => {
    let avoidHideModal = false;

    try {
      if (authService.isSignInWithEmailLink(window.location.href)) {
        const result = await detectIncognito();
        let email =
          emailString ||
          LocalStorage(result.isPrivate).getItem("emailForSignInCrypto");

        if (!email) {
          Model.setData("showAuth", true);
          setRequestEmail(true);
          avoidHideModal = true;

          return Model.updateAlerts({
            message: t("pages.home.buyHome.pleaseEnterEmail"),
            variant: "warning",
          });
        }

        const userInfo = await authService.signInWithEmailLink(
          email,
          window.location.href,
        );

        LocalStorage(result.isPrivate).removeItem("emailForSignInCrypto");
        Model.updateInfoUser(userInfo.user);
      }
    } catch (error) {
      Model.updateAlerts({
        message: error?.message || error,
        variant: "error",
      });
    } finally {
      if (!avoidHideModal) {
        Model.setData("showAuth", false);
      }
    }
  };

  useEffect(() => {
    setEmail("");
    setEmailSent(false);
    setAcceptTerms(false);
    setRequestEmail(false);
    setShowOtpForm(false);
    setOtpCode("");
  }, [state.showAuth]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (!user && window.location.href.includes("oobCode")) {
        firstLoad();
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.showAuth && state.infoUser?.uid) {
      Model.setData("showAuth", false);
      Model.updateAlerts({
        message: "Tu sesión ya está abierta :)",
        variant: "warning",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showAuth, state.infoUser?.uid]);

  if (!state.showAuth || !!state.infoUser?.uid) {
    return <></>;
  }

  return (
    <>
      <div className={styles.container}>
        <Backdrop
          open={state.showAuth}
          className={styles.backdrop}
          onClick={() => Model.setData("showAuth", false)}
        />
        <div className={styles.card}>
          <img src={Logocrom} alt="FROIN" className={styles.logo} />
          {renderContent()}
        </div>
      </div>
    </>
  );
}

export default AuthModal;
